import { Component, Input, OnInit, ViewChild, Output, EventEmitter, OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
    HisSearchResultComponent, HisAuthService, HisUiService, HisNotificationService, HisConfirmationService,
    HisSearchResultRow, HisNavigationService
} from 'hisng';

import { Publication } from 'app/fs/res/publication/model/publication';
import { PublicationService } from 'app/fs/res/publication/service/publication.service';
import { CONFIRMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/confirmation-dialog.component';
import { INFORMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/information-dialog.component';
import { PaginatorState } from 'primeng/paginator';
import { PublicationModuleOptions } from '../../../publication-module.options';


@Component( {
    selector: 'res-publication-search-result-list',
    templateUrl: './publication-search-result-list.component.html'
} )
export class PublicationSearchResultListComponent implements OnInit, OnChanges {

    public readonly moduleOptions = PublicationModuleOptions;

    @Input() compName: string;

    @ViewChild( HisSearchResultComponent, { static: true } ) searchResultListComponent: HisSearchResultComponent;

    @Input() data: HisSearchResultRow[];

    @Input() totalRecords: number;
    @Input() rowsPerPage: number;
    @Input() first: number;

    @Input() showNewSearchButton = true;
    @Input() showChangeSearchButton = true;
    @Input() showNewPublicationMessage = true;
    // Link zum Umschalten zur Tabellenansicht
    @Input() showSwitchRepresentationLink = true;
    // Action buttons
    @Input() showViewButton = true;
    @Input() showEditButton = true;
    @Input() showDeleteButton = true;

    // spezielle Action-Buttons, die vor dem Delete-Button eingefügt werden (z.B. Duplikate ansehen)
    @Input() customActionButtonsTemplate: TemplateRef<any>;

    // Wenn true, dann werden edit- und delete-Actionbuttons bei fehlenden canEdit-, canDelete-Flags disabled
    @Input() disableActionButtons = true;

    @Input() prevPage: string;

    @Output() page = new EventEmitter();

    public currentPage: number;
    public totalPage: number;

    constructor(
        public auth: HisAuthService,
        private confirmationService: HisConfirmationService,
        public publicationService: PublicationService, // not private because used in template
        public hisSearchResultComponent: HisSearchResultComponent,
        private hisUiService: HisUiService,
        private notificationService: HisNotificationService,
        private navigationService: HisNavigationService,
        private route: ActivatedRoute
    ) { }

    ngOnInit() {
        let params = this.route.snapshot.queryParams;
	    let page = Number(params.page);
	    if (page) {
	        this.first = ( page - 1 ) * this.rowsPerPage;
	    }

	    this.page.emit( { first: this.first, rows: this.rowsPerPage } );
    }

    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.data ) { // changes.data.currentValue is equal to @Input() data
            console.log( 'publication search result list: rows changed to', changes.data.currentValue );

            // load additional attributes from backend
            let publicationIds: number[] = this.data.map( searchResultRow => parseInt( searchResultRow.get('Publication.id'), 10 ) );
            console.log('search result publicationIds =', publicationIds);
            if ( publicationIds.length > 0 ) {
                this.publicationService.getPublicationsIncludingOwnImportsByIds( publicationIds )
                    .subscribe( (publications: Publication[]) => {
                        console.log( 'reloaded publications =', publications );
                        // Suchergebnisse nach Publication.id hashen
                        let publicationIdsToSearchResultRows: Map<number, HisSearchResultRow> = 
                            PublicationSearchResultListComponent.hashSearchResultRowsByPublicationIds( this.data );
                        // #332971 Nachgeladene Informationen anhand der Publication.id zu den Suchergebnissen hinzufügen
                        for ( let publication of publications ) {
                            let searchResultRow: HisSearchResultRow = publicationIdsToSearchResultRows.get( publication.id );
                            this.enrichSearchResultRowFromPublication( searchResultRow, publication );
                        }
                    } );
            }

            this.totalPage = ( ( this.totalRecords - ( this.totalRecords % this.rowsPerPage ) ) / this.rowsPerPage ) + 1;
            this.currentPage = ( this.first / this.rowsPerPage ) + 1;
        }
    }

    private static hashSearchResultRowsByPublicationIds( searchResultRows: HisSearchResultRow[] ): Map<number, HisSearchResultRow> {
        let publicationIdsToSearchResultRows = new Map<number, HisSearchResultRow>();
        for ( let searchResultRow of searchResultRows ) {
            let publicationId: number = searchResultRow.get( 'Publication.id' );
            publicationIdsToSearchResultRows.set( publicationId, searchResultRow );
        }
        return publicationIdsToSearchResultRows;
    }
    
    private enrichSearchResultRowFromPublication(searchResultRow: HisSearchResultRow, pub: Publication) {
        // add former hiddenSelectColumns
        searchResultRow.set( 'Publication.qualificationThesis.longtext', pub.qualificationThesis.defaulttext );
        searchResultRow.set( 'Publication.qualificationThesis.hiskeyId', pub.qualificationThesis.hiskeyId );
        searchResultRow.set( 'Publication.publicationCreatorType.hiskeyId', pub.publicationCreatorType.hiskeyId );
        console.log('peerReviewed =', pub.peerReviewedProcess );
        let peerReviewed: boolean = pub.peerReviewedProcess && pub.peerReviewedProcess.id === 1;
        searchResultRow.set( 'Publication.peerReviewed', peerReviewed );
        searchResultRow.set( 'Publication.access.hiskeyId', pub.access.hiskeyId );

        // set all publication creators
        let creatorsList: string[] = [];
        for ( let creator of pub.creators ) {
            let creatorStr = creator.firstname ? creator.firstname + ' ' + creator.creatorname : creator.creatorname;
            creatorsList.push( creatorStr );
        }

        searchResultRow.set( 'creatorNamesStr', creatorsList.join( ', ') );
        searchResultRow.set( 'creatorNamesList', creatorsList );

        let globalIdentifiers: string[] = pub.globalIdentifiers.map(gi => gi.globalIdentifierType.uniquename + ': ' + gi.identifier);
        searchResultRow.set( 'Publication.globalIdentifiers', globalIdentifiers.join(', ') );
    }

    /**
     * Page change function of PrimeNG
     *
     * @param event.pageCount Total number of pages
     * @param event.page  Index of the new page
     * @param event.first Index of the first record
     */
    paginate( event: PaginatorState ) {
        this.page.emit( event );
    }

    /**
     * delete the given publication from database.
     *
     * @param publicationRow the publication to delete
     */
    onDelete( publicationRow: HisSearchResultRow ) {
        console.log('onDelete publicationRow =', publicationRow);
        let publicationId = publicationRow.get( 'Publication.id' );
        this.publicationService.canDeletePublication( publicationId ).subscribe( (canDelete: string) => {
            if ( canDelete === '' ) {
                this.confirm( 'fs.res.publication.confirmDeletePublication', publicationRow, canDelete);
            } else {
                this.confirm( canDelete, publicationRow, canDelete);
            }
        } );
    }

    /**
     * confirmation/information dialog to delete publication
     *
     * @param msg dialog message
     * @param publicationRow (object) to delete
     * @param canDelete identifier publication can be deleted
     */
    private confirm( msg: string, publicationRow: HisSearchResultRow, canDelete: string ) {
        let dialogKey = canDelete === '' ? CONFIRMATION_PDIALOG_KEY : INFORMATION_PDIALOG_KEY;
        let title = publicationRow.get( 'Publication.title' );
        let translatedMsg = canDelete === '' ? this.hisUiService.translate( msg, title ) : canDelete;
        let publicationId = publicationRow.get( 'Publication.id' );
        this.confirmationService.confirm( {
            key: dialogKey,
            message: translatedMsg,
            accept: () => {
                if ( canDelete === '' ) {
                    this.publicationService.deletePublication( publicationId ).subscribe( (deleteSuccess: number) => {
                        if ( deleteSuccess === 1 ) {
                            this.notificationService.deleteSuccessI18nRes();
                            this.hisSearchResultComponent.reloadPageAfterDelete();
                        } else {
                            console.log('delete failed with result =', deleteSuccess);
                        }
                    } );
                } else {
                    console.log('delete not allowed');
                }
            }
        } );
    }

    back() {
        this.navigationService.navigateToLastBackUrl();
    }

    switchAndClearListTable( view: string, isShowList: boolean ) {
        this.hisSearchResultComponent.clearRowsAndColumns();
        this.publicationService.switchListTable( view, isShowList );
    }
}
